import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, BehaviorSubject } from "rxjs";
import { map, tap, switchMap, takeUntil } from "rxjs/operators";

import { environment } from "../../../../../../apps/e-forsyning/src/environments/environment";
import { PushNotificationService, NotificationPayload } from "./push-notification.service";
import { AktuelBrugerObserver } from "@e-forsyning/common/bruger";

export interface DocumentNotification {
    id: string;
    title: string;
    documentType: string;
    installationId: string;
    installationName: string;
    timestamp: Date;
    isRead: boolean;
    documentUrl?: string;
}

export interface NotificationSettings {
    enabled: boolean;
    documentTypes: string[];
    installations: string[];
    emailNotifications: boolean;
    smsNotifications: boolean;
}

@Injectable({ providedIn: "root" })
export class DocumentNotificationService {
    private readonly http = inject(HttpClient);
    private readonly pushNotificationService = inject(PushNotificationService);
    private readonly aktuelBrugerObserver = inject(AktuelBrugerObserver);

    private readonly apiUrl = environment.pushApiUrl;

    private readonly notificationsSubject = new BehaviorSubject<DocumentNotification[]>([]);
    public readonly notifications$ = this.notificationsSubject.asObservable();

    constructor() {
        // Abonnere på push notifikationer og opdatere notifikationslisten når der modtages nye
        this.pushNotificationService.onPushNotification().subscribe((payload) => {
            // Når en push notifikation modtages, opdater notifikationslisten
            // Dette sikrer at nye notifikationer vises i NotificationCenterComponent
            this.refreshNotifications();
        });
    }

    /**
     * Henter alle notifikationer for den aktuelle bruger
     */
    public getNotifications(): Observable<DocumentNotification[]> {
        return this.aktuelBrugerObserver.valueChanges.pipe(
            switchMap((bruger) => {
                if (!bruger?.id) {
                    // Fallback til test user ID hvis ikke logget ind
                    const params = new HttpParams().set("userId", "1");
                    return this.http.get<DocumentNotification[]>(`${this.apiUrl}/api/notifications/documents`, { params });
                }
                const params = new HttpParams().set("userId", bruger.id.toString());
                return this.http.get<DocumentNotification[]>(`${this.apiUrl}/api/notifications/documents`, { params });
            }),
            tap((notifications) => this.notificationsSubject.next(notifications))
        );
    }

    /**
     * Markerer en notifikation som læst
     */
    public markAsRead(notificationId: string): Observable<void> {
        return this.aktuelBrugerObserver.valueChanges.pipe(
            switchMap((bruger) => {
                if (!bruger?.id) {
                    throw new Error("Bruger ikke logget ind");
                }
                const params = new HttpParams().set("userId", bruger.id.toString());
                return this.http.put<void>(`${this.apiUrl}/api/notifications/documents/${notificationId}/read`, {}, { params });
            }),
            tap(() => {
                const notifications = this.notificationsSubject.value.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n));
                this.notificationsSubject.next(notifications);
            })
        );
    }

    /**
     * Markerer alle notifikationer som læst
     */
    public markAllAsRead(): Observable<void> {
        return this.aktuelBrugerObserver.valueChanges.pipe(
            switchMap((bruger) => {
                if (!bruger?.id) {
                    throw new Error("Bruger ikke logget ind");
                }
                const params = new HttpParams().set("userId", bruger.id.toString());
                return this.http.put<void>(`${this.apiUrl}/api/notifications/documents/read-all`, {}, { params });
            }),
            tap(() => {
                const notifications = this.notificationsSubject.value.map((n) => ({ ...n, isRead: true }));
                this.notificationsSubject.next(notifications);
            })
        );
    }

    /**
     * Sletter en notifikation
     */
    public deleteNotification(notificationId: string): Observable<void> {
        return this.aktuelBrugerObserver.valueChanges.pipe(
            switchMap((bruger) => {
                if (!bruger?.id) {
                    throw new Error("Bruger ikke logget ind");
                }
                const params = new HttpParams().set("userId", bruger.id.toString());
                return this.http.delete<void>(`${this.apiUrl}/api/notifications/documents/${notificationId}`, { params });
            }),
            tap(() => {
                const notifications = this.notificationsSubject.value.filter((n) => n.id !== notificationId);
                this.notificationsSubject.next(notifications);
            })
        );
    }

    /**
     * Henter notifikationsindstillinger
     */
    public getNotificationSettings(): Observable<NotificationSettings> {
        return this.aktuelBrugerObserver.valueChanges.pipe(
            switchMap((bruger) => {
                if (!bruger?.id) {
                    throw new Error("Bruger ikke logget ind");
                }
                const params = new HttpParams().set("userId", bruger.id.toString());
                return this.http.get<NotificationSettings>(`${this.apiUrl}/api/notifications/settings`, { params });
            })
        );
    }

    /**
     * Opdaterer notifikationsindstillinger
     */
    public updateNotificationSettings(settings: Partial<NotificationSettings>): Observable<NotificationSettings> {
        return this.aktuelBrugerObserver.valueChanges.pipe(
            switchMap((bruger) => {
                if (!bruger?.id) {
                    throw new Error("Bruger ikke logget ind");
                }
                const params = new HttpParams().set("userId", bruger.id.toString());
                return this.http.put<NotificationSettings>(`${this.apiUrl}/api/notifications/settings`, settings, { params });
            })
        );
    }

    /**
     * Henter antal ulæste notifikationer
     */
    public getUnreadCount(): Observable<number> {
        return this.notifications$.pipe(map((notifications) => notifications.filter((n) => !n.isRead).length));
    }

    /**
     * Opdaterer notifikationslisten ved at hente nye notifikationer fra serveren
     */
    public refreshNotifications(): void {
        this.getNotifications().subscribe({
            next: (notifications) => {},
            error: (error) => {
                console.error("❌ Fejl ved opdatering af notifikationsliste:", error);
            }
        });
    }

    /**
     * Opretter en lokal notifikation når der er nye dokumenter
     */
    public showDocumentNotification(document: DocumentNotification): void {
        if (!this.pushNotificationService.isNotificationEnabled()) {
            return;
        }

        const notificationPayload: NotificationPayload = {
            title: "Nyt dokument",
            body: `${document.documentType} fra ${document.installationName}`,
            icon: "/assets/icons/icon-192x192.png",
            badge: "/assets/icons/icon-72x72.png",
            tag: "document-notification",
            data: {
                documentId: document.id,
                documentUrl: document.documentUrl,
                installationId: document.installationId
            },
            actions: [
                {
                    action: "view",
                    title: "Se dokument",
                    icon: "/assets/icons/view-icon.png"
                },
                {
                    action: "dismiss",
                    title: "Luk",
                    icon: "/assets/icons/close-icon.png"
                }
            ]
        };

        // Dette vil blive håndteret af service worker
    }

    /**
     * Håndterer klik på notifikation
     */
    public handleNotificationClick(notification: NotificationPayload): void {
        if (notification.data?.documentUrl) {
            // Åbn dokumentet i en ny fane
            window.open(notification.data.documentUrl, "_blank");
        }

        // Marker som læst
        if (notification.data?.documentId) {
            this.markAsRead(notification.data.documentId).subscribe();
        }
    }

    /**
     * Håndterer notifikations actions
     */
    public handleNotificationAction(notification: NotificationPayload, action: string): void {
        switch (action) {
            case "view":
                this.handleNotificationClick(notification);
                break;
            case "dismiss":
                // Luk notifikationen
                break;
            default:
                break;
        }
    }

    /**
     * Tjekker for nye dokumenter og sender notifikationer
     */
    public checkForNewDocuments(): Observable<DocumentNotification[]> {
        return this.http.get<DocumentNotification[]>(`${this.apiUrl}/api/documents/new`).pipe(
            tap((newDocuments) => {
                newDocuments.forEach((document) => {
                    this.showDocumentNotification(document);
                });
            })
        );
    }

    /**
     * Abonnerer på real-time dokument opdateringer
     */
    public subscribeToDocumentUpdates(): Observable<DocumentNotification> {
        // Dette kunne implementeres med WebSockets eller Server-Sent Events
        return new Observable((observer) => {
            // Implementer real-time subscription her
            // For nu returnerer vi en tom observable
        });
    }
}
